// /**
//  * Products - list
//  */

// React
import React, {useContext} from 'react';

// UI
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';

// Type
import {Variant} from '../interfaces';
import {Link} from 'react-router-dom';
import Can from '../../../../components/Can';
import AuthContext from '../../../../authContext';

type Data = Variant;
type Props = {
  data: any;
  productName: string;
  handleOpenConfirmDisable: (id: number) => void;
  handleOpen: (id: number, type: string) => void;
  changeStatus: any;
};

type Ord = 'asc' | 'desc';

interface HeadCell {
  id: keyof Data;
  label: string;
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    body: {
      fontSize: 12,
      verticalAlign: 'baseline',
    },
  })
)(TableCell);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      '& .MuiTableCell-root': {
        padding: '12px',
      },
      '& .MuiIconButton-sizeSmall': {
        padding: '8px',
      },
    },
    paper: {
      width: '100%',
    },
    table: {
      // minWidth: 750,
    },
    columnText: {
      padding: '0',
    },
    dot: {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      left: '-8px',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    textMute: {
      fontSize: '12px',
      color: 'rgba(130, 127, 127, 0.72)',
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
  a: {[key in Key]: number | string},
  b: {[key in Key]: number | string}
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const authContext = useContext(AuthContext);
  const {order, orderBy, onRequestSort} = props;
  const classes = useStyles();

  let headCells: HeadCell[] = [];

  Can({
    role: authContext.user.role,
    perform: 'products:inventory',
    yes: () => {
      headCells = [
        // {
        //   id: 'productName',
        //   label: 'نام',
        // },
        {
          id: 'id',
          label: 'شناسه تنوع',
        },
        {
          id: 'code',
          label: 'کد تنوع',
        },
        {
          id: 'sellerName',
          label: 'فروشنده',
        },
        {
          id: 'providerName',
          label: 'تامین کننده',
        },
        {
          id: 'inventory',
          label: 'موجودی',
        },
      ];
    },
    no: () => {
      headCells = [
        {
          id: 'id',
          label: 'شناسه تنوع',
        },
        {
          id: 'code',
          label: 'کد تنوع',
        },
        {
          id: 'sellerName',
          label: 'فروشنده',
        },
        {
          id: 'providerName',
          label: 'تامین کننده',
        },
      ];
    },
  });

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => {
          return (
            <StyledTableCell
              key={headCell.id}
              align="center"
              sortDirection={orderBy === headCell.id ? order : false}
              data-cy={headCell.id}
            >
              <span className={classes.columnText}>{headCell.label}</span>
            </StyledTableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('id');
  const [dense] = React.useState(false);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <TableContainer>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
          aria-label="enhanced table"
        >
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
          />
          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow key={index}>
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                      data-cy="tbid"
                    >
                      <a
                        style={{textDecoration: 'none'}}
                        href={`/your-products/2704/variant/${row.id}`}
                      >
                        {row.id}
                      </a>
                      <p className={classes.textMute}>موجودی: {row.stock}</p>
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                      data-cy="code"
                    >
                      {row.code}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                      data-cy="sellerName"
                    >
                      {row.sellerCode
                        ? `${row.sellerName}(${row.sellerCode})`
                        : row.sellerName}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                      data-cy="providerName"
                    >
                      {row.providerCode
                        ? `${row.providerName}(${row.providerCode})`
                        : row.providerName}
                    </StyledTableCell>
                    {/* <Can
                      role={authContext.user.role}
                      perform="products:inventory"
                      yes={() => (
                        <TableCell
                          component="th"
                          id={labelId}
                          scope="row"
                          align="center"
                        >
                          {row.stock}
                        </TableCell>
                      )}
                    /> */}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
